/**
 * Training card frames -> the SAME Redux actions a live sales call dispatches.
 *
 * Training exists to get reps used to the live-call screen, so its cards are
 * not drawn by training components: they go into conversationSlice,
 * landmineSlice and competitorSlice and are rendered by ConversationFeed,
 * LandmineBar (in CombinedInfoBar) and CompetitorAlert, exactly as on a call.
 *
 * Pure on purpose: the timestamp is passed in, so the mapping is pinned by
 * tests without a clock.
 */
import type { AnyAction } from '@reduxjs/toolkit';

import { addAnswer, addQuestion, addStoredAnswer } from '../store/conversationSlice';
import { addCompetitorAlert } from '../store/slices/competitorSlice';
import { addLandmineAlert } from '../store/slices/landmineSlice';
import type {
  CompetitorCardData,
  KbSpeaker,
  LandmineCardData,
  QuestionDetectedData,
  StoredAnswerData,
} from '../services/kbLiveFrames';

/** In training the AI customer raises every item, so every alert is theirs. */
const ALERT_SPEAKER = 'customer';
/** The server picked this item for the rep; there is no detection score. */
const TRAINING_CONFIDENCE = 1;
const TRAINING_MATCH_TYPE = 'training';
const DEFAULT_LANDMINE_TYPE = 'legal_risk';
const DEFAULT_PRIORITY = 'medium';

export type TranscriptSpeaker = 'customer' | 'salesperson';

const TRANSCRIPT_SPEAKER: Record<KbSpeaker, TranscriptSpeaker> = {
  ai_customer: 'customer',
  salesperson: 'salesperson',
};

export const toTranscriptSpeaker = (speaker: KbSpeaker): TranscriptSpeaker => TRANSCRIPT_SPEAKER[speaker];

const asPoints = (items: string[]): Array<{ point: string }> => (items ?? []).map((point) => ({ point }));

export function questionDetectedActions(data: QuestionDetectedData, timestamp: string): AnyAction[] {
  return [
    addQuestion({
      question: data?.question ?? '',
      category: data?.category ?? '',
      relevance_score: TRAINING_CONFIDENCE,
      timestamp,
    }),
  ];
}

/**
 * Training has no AI-generated answer. `addAnswer` with the stored text moves
 * the entry from pending to completed; without it ConversationEntry would
 * show "AI is processing" forever.
 */
export function storedAnswerActions(data: StoredAnswerData, timestamp: string): AnyAction[] {
  const question = data?.question ?? '';
  const storedAnswer = data?.storedAnswer ?? '';
  return [
    addStoredAnswer({ question, stored_answer: storedAnswer, timestamp }),
    addAnswer({ question, answer: storedAnswer, timestamp }),
  ];
}

export function landmineAlertActions(data: LandmineCardData, timestamp: string): AnyAction[] {
  const trigger = data?.trigger ?? '';
  return [
    addLandmineAlert({
      trigger,
      matched_text: data?.matchedText ?? '',
      matched_phrase: trigger,
      similarity_score: TRAINING_CONFIDENCE,
      semantic_match: false,
      landmine_type: DEFAULT_LANDMINE_TYPE,
      category: '',
      priority: DEFAULT_PRIORITY,
      can_say: asPoints(data?.canSay),
      cannot_say: asPoints(data?.cannotSay),
      quick_tips: data?.quickTips ?? '',
      detected_at: timestamp,
      timestamp,
      speaker: ALERT_SPEAKER,
    }),
  ];
}

export function competitorAlertActions(data: CompetitorCardData, timestamp: string): AnyAction[] {
  return [
    addCompetitorAlert({
      competitor: data?.name ?? '',
      matched_text: data?.matchedText ?? '',
      confidence: TRAINING_CONFIDENCE,
      match_type: TRAINING_MATCH_TYPE,
      advantages: asPoints(data?.advantages),
      weaknesses: asPoints(data?.weaknesses),
      talking_points: data?.talkingPoints ?? '',
      category: '',
      detected_at: timestamp,
      timestamp,
      speaker: ALERT_SPEAKER,
    }),
  ];
}
